/**
 * UI Context для LLM ассистента
 *
 * Позволяет компонентам передавать свой текущий UI-контекст ассистенту.
 * Контекст сохраняется с коротким TTL (15 минут) и автоматически
 * подмешивается в запросы к LLM с высоким приоритетом.
 */

import { appendEntityContext } from './rpc';
import type { RepositoryEntity } from '@ayocore/platform/server/types/repository';

/**
 * Класс для работы с LLM ассистентом на клиенте
 *
 * Предоставляет методы для установки UI-контекста, который автоматически
 * подмешивается в запросы к LLM с высоким приоритетом.
 */
class Assistant {
  private static instance: Assistant | null = null;

  /**
   * Получить экземпляр синглтона Assistant
   */
  static getInstance(): Assistant {
    if (!Assistant.instance) {
      Assistant.instance = new Assistant();
    }
    return Assistant.instance;
  }

  /**
   * Установить UI-контекст сущности для текущей сессии ассистента.
   *
   * Резолвит данные сущности через RPC (getSnapshot) и сохраняет их в контекст сессии.
   * Key генерируется автоматически как className__uuid (например, LeadRepository__123e4567-...).
   *
   * assistantSessionId автоматически получается на сервере из request context
   * (сессия BASE_ASSISTANT для текущего пользователя)
   *
   * @param entity - Сущность репозитория в формате {type, uuid} (RepositoryEntity)
   * @param options - Опциональные параметры (expiresAt, key, metadata)
   *
   * @example
   * ```ts
   * const entity = await LeadRepository.getEntity(leadId);
   * if (entity) {
   *   await assistant.setEntityContext(entity, {
   *     expiresAt: new Date(Date.now() + 15 * 60 * 1000), // 15 минут
   *   });
   * }
   * ```
   */
  async setEntityContext(
    entity: RepositoryEntity,
    options?: {
      /** Дата истечения контекста (по умолчанию 15 минут) */
      expiresAt?: Date | null;
      /** Ключ для upsert (по умолчанию генерируется как className__uuid) */
      key?: string | null;
      /** Опциональные метаданные */
      metadata?: unknown | null;
    }
  ): Promise<void> {
    // UI-контекст сохраняется с TTL 15 минут по умолчанию
    const expiresAt = options?.expiresAt ?? new Date(Date.now() + 15 * 60 * 1000);

    const result = await appendEntityContext({
      entity,
      key: options?.key ?? null, // Если не передан, сгенерируется автоматически
      expiresAt,
      metadata: options?.metadata ?? null,
    });

    if (!result.success) {
      throw new Error(result.error || 'Failed to set entity context');
    }
  }
}

/**
 * Экспорт синглтона для удобного использования
 */
export const assistant = Assistant.getInstance();

/**
 * Экспорт класса для тестирования и расширения
 */
export { Assistant };
